import { Fragment, createContext, useContext, useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import { t } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { readPreference, writePreference } from '../../shell/devicePreference';
import styles from './StagedLayout.module.css';

export interface FlowedTile {
  key: string;
  span: number;
  node: ReactNode;
}

/** 🔒 One band of the page (decision 1215): its tiles drawn side by side at one height, or the stack of stage cards. */
export type StagedBand = { kind: 'tiles'; key: string; tiles: readonly FlowedTile[] } | { kind: 'stages' };

export interface StagedLayoutProps {
  /** The tiles before the stage cards, the general information among them. */
  before: readonly FlowedTile[];
  stages: ReactNode;
  /** 🔒 A page-level banner at the head of the main column, under the rail on a phone (decision 1219). */
  notice?: ReactNode;
  /** 🔒 Below the desktop breakpoint, the stage cards straight after the banner with the current card first (decision 1215). */
  phoneCurrentFirst?: boolean;
  /** The tile key the stack stands in for, so the page's tile order still reads it. */
  stagesKey: string;
  after: readonly FlowedTile[];
  /**
   * 🔒 Where the record type names bands, the main column reads as them top to
   * bottom in place of `before` and `after` (decision 1215). A band left with no
   * tile is not drawn.
   */
  bands?: readonly StagedBand[] | null;
  panelTitle: string;
  panel: ReactNode;
  /** The condensed indicator the collapsed panel and the phone draw: I/E/N/D/W, A/I/N. */
  rail: ReactNode;
  /** Where the collapsed state is remembered per browser. */
  storageKey: string;
}

const read = (key: string): boolean => readPreference<boolean>(key, (raw) => raw === 'rail', false);

/** What a control inside the layout calls to open the progress panel; null outside a staged layout. */
const OpenPanelContext = createContext<(() => void) | null>(null);

/**
 * 🔒 Open Deal Progress (decision 1935; T1750): the panel opened where it is
 * folded to the rail, brought into view and given the focus, so the ladder the
 * button names is where the person is looking. On a phone the panel does not
 * draw, so the stage cards are brought into view instead.
 */
export function useOpenProgressPanel(): (() => void) | null {
  return useContext(OpenPanelContext);
}

/**
 * A staged record's page (decision 0293; figures 81 and 82): the tiles and the
 * stage cards flow down the main column at their own height, and the progress
 * panel stands pinned at the right, collapsible to the condensed rail. 🔨 Not
 * the Tile Canvas: a Deal and a Lead are worked top to bottom, and their stage
 * cards need the width of the column, not a block of it.
 */
export function StagedLayout({ before, stages, notice = null, phoneCurrentFirst = false, stagesKey, after, bands = null, panelTitle, panel, rail, storageKey }: StagedLayoutProps) {
  const [collapsed, setCollapsed] = useState(() => read(storageKey));
  /** Set by Open Deal Progress and cleared when the focus leaves the panel, so the panel draws as the one called for. */
  const [called, setCalled] = useState(false);
  const [calls, setCalls] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const openPanel = () => {
    if (collapsed) {
      setCollapsed(false);
      writePreference(storageKey, 'open');
    }
    setCalled(true);
    setCalls((n) => n + 1);
  };
  useEffect(() => {
    if (calls === 0) return;
    const panelNode = panelRef.current;
    // A panel the breakpoint hides has no box, and the stage cards are the ladder a phone reads.
    const shown = panelNode !== null && panelNode.getClientRects().length > 0;
    const target = shown ? panelNode : stackRef.current;
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    if (shown) panelNode.focus({ preventScroll: true });
  }, [calls]);
  const leavePanel = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setCalled(false);
  };
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writePreference(storageKey, next ? 'rail' : 'open');
  };
  const flow = (tiles: readonly FlowedTile[]) =>
    tiles.map((tile) => (
      <div key={tile.key} data-flow-span={String(tile.span)}>
        {tile.node}
      </div>
    ));
  const stack = (
    <div ref={stackRef} className={styles.stages} data-stage-cards data-record-tile={stagesKey}>
      {stages}
    </div>
  );
  return (
    <OpenPanelContext.Provider value={openPanel}>
      <div className={styles.layout} data-record-layout="staged" data-progress-panel={collapsed ? 'rail' : 'open'} data-phone-order={phoneCurrentFirst ? 'current-first' : 'bands'}>
        <div className={styles.main}>
          {notice ? <div className={styles.notice} data-staged-notice="true">{notice}</div> : null}
          {bands ? (
            /* 🔒 The bands, top to bottom (decision 1215): a band's tiles stand side by side at one height, one gap step between bands. */
            bands.map((band) => (band.kind === 'stages' ? <Fragment key="stages">{stack}</Fragment> : band.tiles.length === 0 ? null : (
              <div key={band.key} className={styles.band} data-staged-band={band.key} data-staged-band-tiles={String(band.tiles.length)}>
                {band.tiles.map((tile) => (
                  <div key={tile.key} className={styles.bandCell} data-band-cell={tile.key}>
                    {tile.node}
                  </div>
                ))}
              </div>
            )))
          ) : (
            <>
              {before.length > 0 ? <div className={styles.flow}>{flow(before)}</div> : null}
              {stack}
              {after.length > 0 ? <div className={styles.flow}>{flow(after)}</div> : null}
            </>
          )}
        </div>
        <aside className={styles.side} aria-label={panelTitle} data-progress-side>
          <div ref={panelRef} className={styles.panel} tabIndex={-1} data-progress-called={called ? 'true' : 'false'} onBlur={leavePanel}>
            <div className={styles.panelHead}>
              <span className={styles.panelTitle}>{panelTitle}</span>
              <button type="button" className={styles.toggle} onClick={toggle} aria-expanded="true" aria-label={t('record.panel.collapse')} data-progress-toggle="collapse">
                <Icon meaning="collapse" size="inline" />
              </button>
            </div>
            <div className={styles.panelBody}>{panel}</div>
          </div>
          <div className={styles.rail} data-progress-rail>
            <button type="button" className={styles.toggle} onClick={toggle} aria-expanded="false" aria-label={t('record.panel.expand')} data-progress-toggle="expand">
              <Icon meaning="expand" size="inline" />
            </button>
            {rail}
          </div>
        </aside>
      </div>
    </OpenPanelContext.Provider>
  );
}
